import React, {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import useLocalStorage from 'react-localstorage-hook';
import { useSearchParams } from 'react-router';

import useDebounce from '@hooks/common/useDebounce';

import { areAllChainsSelected } from '@helpers/common/chain.helpers';
import {
  getBooleanSearchParam,
  getStringArraySearchParam,
  toggleParam,
} from '@helpers/common/searchParams.helpers';
import { removeAnyTabParam } from '@helpers/vaults/vault.helpers';

import { toBN } from '@utils/formaters.utils';

import {
  AVAILABLE_CHAINS_LIST,
  CHAINS_FILTER,
  CHAIN_ID,
} from '@constants/common/chain.constants';
import {
  DEFAULT_DELAY,
  SortDestination,
} from '@constants/common/common.constants';
import { VAULTS_FILTERS } from '@constants/common/localStorage.constants';
import {
  CHAIN_FILTER_PARAM,
  SEARCH_PARAM,
  SORT_BY_PARAM,
  SORT_DIRECTION_PARAM,
  SWOW_UNIQ_BY_FARMS,
} from '@constants/common/params.constants';
import {
  ALL_FARM_TYPES,
  DEFAULT_LEVEL_FROM,
  DEFAULT_LEVEL_TO,
  DEFAULT_VAULTS_TAB,
  FarmsFilterTab,
  SortBy,
  VaultsFilterTab,
} from '@constants/vaults/common.constants';
import {
  FARM_TYPES_PARAM,
  PLATFORMS_PARAM,
  PROFIT_PERCENT_FROM_PARAM,
  PROFIT_PERCENT_TO_PARAM,
  PROFIT_TOKENS_PARAM,
  RANGES_PARAM,
  RISK_LEVEL_FROM_PARAM,
  RISK_LEVEL_TO_PARAM,
  SMALL_TVL_PARAM,
  STRATEGY_FARM_PARAM,
  TAB_PARAM,
  TAGS_PARAM,
  V3_FARM_TYPES_PARAM,
} from '@constants/vaults/params.constants';

export type VaultsFiltersContextType = {
  tab: string;
  search: string;
  sortBy: string;
  tags: string[];
  ranges: string[];
  selectedChains: CHAIN_ID[];
  strategy: string | undefined;
  v3Type: string | undefined;
  farmTypes: string[];
  platforms: string[];
  profitTokens: string[];
  riskLevelTo: number;
  riskLevelFrom: number;
  profitPercentTo: number;
  profitPercentFrom: number;
  isShowSmallTvls: boolean;
  counterFilters: number;
  sortDirection: SortDestination;
  debounceSearchValue: string;
  singleFarmApi: boolean | undefined;
  minRiskLevelApi: number;
  maxRiskLevelApi: number;
  minProfitPercentApi: number;
  maxProfitPercentApi: number;
  isArchivedMode: boolean;
  disabledV3TypeFilter: boolean;
  isUniqByFarms?: boolean;
};

export type VaultsFiltersActionsType = {
  onClear: () => void;
  onUpdateFilters: () => void;
  onToggleSmallTVLs: () => void;
  onToggleArchivedMode: () => void;
  onSearch: (value: string) => void;
  onToggleSortBy: (value: string) => void;
  onUpdateSortBy: (value: string) => void;
  onUpdateProfitTokens: (value: string[]) => void;
  onUpdateTags: (values: { value: string; label: string }[]) => void;
  onUpdateRanges: (values: { value: string; label: string }[]) => void;
  onUpdateStrategy: (value: string | undefined) => void;
  onUpdateV3Type: (value: string | undefined) => void;
  onUpdatePlatforms: (values: number[]) => void;
  onUpdateRiskLevel: (values: number[]) => void;
  onUpdateProfitPercents: (values: number[]) => void;
  onUpdateFarmTypes: (values: string[]) => void;
  onToggleStrategy: (value: string | number) => void;
  onToggleV3Type: (value: string | number) => void;
  onToggleFarmTypes: (value: string | number) => void;
  onUpdateSortDirection: (direction: SortDestination) => void;
  onToggleTab: (type: number | string) => void;
  onChangeChain: (chainId: number, onClick?: () => void) => void;
};

export let vaultsFiltersActions: VaultsFiltersActionsType;

export const VaultsFiltersContext = createContext<VaultsFiltersContextType>({
  tags: [],
  ranges: [],
  search: '',
  strategy: undefined,
  v3Type: undefined,
  farmTypes: [],
  platforms: [],
  profitTokens: [],
  counterFilters: 0,
  isShowSmallTvls: false,
  sortBy: SortBy.Default,
  debounceSearchValue: '',
  singleFarmApi: undefined,
  tab: DEFAULT_VAULTS_TAB,
  isArchivedMode: false,
  riskLevelTo: DEFAULT_LEVEL_FROM,
  riskLevelFrom: DEFAULT_LEVEL_TO,
  profitPercentFrom: DEFAULT_LEVEL_FROM,
  profitPercentTo: DEFAULT_LEVEL_TO,
  sortDirection: SortDestination.Desc,
  minRiskLevelApi: DEFAULT_LEVEL_FROM,
  maxRiskLevelApi: DEFAULT_LEVEL_TO,
  minProfitPercentApi: DEFAULT_LEVEL_FROM,
  maxProfitPercentApi: DEFAULT_LEVEL_TO,
  selectedChains: AVAILABLE_CHAINS_LIST,
  disabledV3TypeFilter: false,
  isUniqByFarms: undefined,
});

export const useVaultsFilters = (): VaultsFiltersContextType =>
  useContext(VaultsFiltersContext);

const VaultsFiltersContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isArchivedMode, setArchivedMode] = useState(false);

  const [filtersInStorage, setFiltersInStorage] =
    useLocalStorage(VAULTS_FILTERS);

  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo(() => {
    const search = searchParams.get(SEARCH_PARAM) || '';
    const tab = searchParams.get(TAB_PARAM) || DEFAULT_VAULTS_TAB;
    const sortBy = searchParams.get(SORT_BY_PARAM) || SortBy.Default;
    const strategy = searchParams.get(STRATEGY_FARM_PARAM) || undefined;
    const v3Type = searchParams.get(V3_FARM_TYPES_PARAM) || undefined;
    const tags = getStringArraySearchParam(searchParams, TAGS_PARAM) || [];
    const ranges = getStringArraySearchParam(searchParams, RANGES_PARAM) || [];
    const farmTypes = getStringArraySearchParam(searchParams, FARM_TYPES_PARAM);
    const isShowSmallTvls = getBooleanSearchParam(
      searchParams,
      SMALL_TVL_PARAM,
    );
    const platforms =
      getStringArraySearchParam(searchParams, PLATFORMS_PARAM) || [];
    const profitTokens =
      getStringArraySearchParam(searchParams, PROFIT_TOKENS_PARAM) || [];
    const riskLevelTo =
      Number(searchParams.get(RISK_LEVEL_TO_PARAM)) || DEFAULT_LEVEL_TO;
    const riskLevelFrom =
      Number(searchParams.get(RISK_LEVEL_FROM_PARAM)) || DEFAULT_LEVEL_FROM;
    const profitPercentTo =
      Number(searchParams.get(PROFIT_PERCENT_TO_PARAM)) || DEFAULT_LEVEL_TO;
    const profitPercentFrom =
      Number(searchParams.get(PROFIT_PERCENT_FROM_PARAM)) || DEFAULT_LEVEL_FROM;
    const sortDirection = (searchParams.get(SORT_DIRECTION_PARAM) ||
      SortDestination.Desc) as SortDestination;
    const isUniqByFarms = !!searchParams.get(SWOW_UNIQ_BY_FARMS) || undefined;

    return {
      tab,
      tags,
      v3Type,
      ranges,
      sortBy,
      search,
      strategy,
      platforms,
      farmTypes,
      sortDirection,
      riskLevelFrom,
      riskLevelTo,
      profitPercentFrom,
      profitPercentTo,
      isShowSmallTvls,
      profitTokens,
      ...(isUniqByFarms && { isUniqByFarms }),
    };
  }, [searchParams]);

  const counterFilters = useMemo(
    () =>
      [
        ...getStringArraySearchParam(searchParams, FARM_TYPES_PARAM),
        ...getStringArraySearchParam(searchParams, STRATEGY_FARM_PARAM),
        ...getStringArraySearchParam(searchParams, V3_FARM_TYPES_PARAM),
        ...getStringArraySearchParam(searchParams, CHAIN_FILTER_PARAM),
        searchParams.has(TAGS_PARAM),
        searchParams.has(SEARCH_PARAM),
        searchParams.has(SORT_BY_PARAM),
        searchParams.has(RANGES_PARAM),
        searchParams.has(PLATFORMS_PARAM),
        searchParams.has(PROFIT_TOKENS_PARAM),
        searchParams.has(RISK_LEVEL_FROM_PARAM) ||
          searchParams.has(RISK_LEVEL_TO_PARAM),
        searchParams.has(PROFIT_PERCENT_FROM_PARAM) ||
          searchParams.has(PROFIT_PERCENT_TO_PARAM),
      ].filter((item) => item).length,
    [searchParams],
  );

  const selectedChains = useMemo(() => {
    const chainsParams = searchParams.get(CHAIN_FILTER_PARAM);
    let chains: CHAIN_ID[] = [];
    chains = chainsParams && JSON.parse(chainsParams);
    return Array.isArray(chains) && chains.length
      ? chains.filter((chainId: number) => CHAINS_FILTER.includes(chainId))
      : AVAILABLE_CHAINS_LIST;
  }, [searchParams]);

  const debounceSearchValue = useDebounce(params.search, {
    delay: DEFAULT_DELAY,
  });

  const singleFarmApi = useMemo(() => {
    if (!params.strategy) {
      return undefined;
    }
    return params.strategy === VaultsFilterTab.Single;
  }, [params]);

  const disabledV3TypeFilter = useMemo(() => {
    if (!params.farmTypes.length) {
      return false;
    }
    return !params.farmTypes.includes(FarmsFilterTab.V3);
  }, [params]);

  const calculateRiskLevel = (value: number) =>
    toBN(value).times(0.05).ceil().toNumber();

  const minRiskLevelApi = useMemo(
    () => calculateRiskLevel(params.riskLevelFrom),
    [params],
  );
  const maxRiskLevelApi = useMemo(
    () => calculateRiskLevel(params.riskLevelTo),
    [params],
  );

  const minProfitPercentApi = useMemo(
    () => Number(toBN(params.profitPercentFrom).div(100).toFixed()),
    [params],
  );
  const maxProfitPercentApi = useMemo(
    () => Number(toBN(params.profitPercentTo).div(100).toFixed()),
    [params],
  );

  vaultsFiltersActions = {
    onToggleArchivedMode: () => {
      setArchivedMode((prevState) => !prevState);
    },
    onToggleSmallTVLs: () => {
      const newShowSmallTvls = !params.isShowSmallTvls;
      if (newShowSmallTvls) {
        searchParams.set(SMALL_TVL_PARAM, String(newShowSmallTvls));
      } else {
        searchParams.delete(SMALL_TVL_PARAM);
      }
      setSearchParams(searchParams);
    },
    onSearch: (value: string) => {
      if (!value) {
        searchParams.delete(SEARCH_PARAM);
      } else {
        searchParams.set(SEARCH_PARAM, value);
      }
      setSearchParams(searchParams);
    },
    onToggleSortBy: (newSortBy: string) => {
      const currentSortBy = searchParams.get(SORT_BY_PARAM);
      const currentSortDirection = searchParams.get(
        SORT_DIRECTION_PARAM,
      ) as SortDestination;

      let newSortDirection: SortDestination | undefined;

      if (!currentSortBy) {
        newSortDirection = SortDestination.Desc;
      } else if (newSortBy === currentSortBy) {
        if (currentSortDirection === SortDestination.Desc) {
          newSortDirection = SortDestination.Asc;
        } else {
          newSortDirection = SortDestination.Desc;
        }
      } else {
        newSortDirection = SortDestination.Desc;
      }

      if (newSortDirection) {
        searchParams.set(SORT_BY_PARAM, newSortBy);

        if (newSortDirection === SortDestination.Desc) {
          searchParams.set(SORT_DIRECTION_PARAM, newSortDirection);
        } else if (newSortDirection === SortDestination.Asc) {
          searchParams.set(SORT_DIRECTION_PARAM, newSortDirection);
        }
      } else {
        searchParams.delete(SORT_BY_PARAM);
        searchParams.delete(SORT_DIRECTION_PARAM);
      }

      setSearchParams(searchParams);
    },

    onUpdateSortBy: (newSortBy: string) => {
      if (newSortBy === SortBy.Default) {
        searchParams.delete(SORT_BY_PARAM);
      } else {
        searchParams.set(SORT_BY_PARAM, newSortBy);
      }
      setSearchParams(searchParams);
    },
    onToggleStrategy: (value: string | number) => {
      searchParams.set(STRATEGY_FARM_PARAM, String(value));
      setSearchParams(searchParams);
    },
    onToggleV3Type: (value: string | number) => {
      if (disabledV3TypeFilter) return;

      searchParams.set(V3_FARM_TYPES_PARAM, String(value));
      setSearchParams(searchParams);
    },
    onUpdateStrategy: (value: string | undefined) => {
      if (value) {
        searchParams.set(STRATEGY_FARM_PARAM, String(value));
        setSearchParams(searchParams);
      }
    },
    onUpdateV3Type: (value: string | undefined) => {
      if (value) {
        searchParams.set(V3_FARM_TYPES_PARAM, String(value));
        setSearchParams(searchParams);
      }
    },
    onToggleFarmTypes: (value: string | number) => {
      const isV3 = value === FarmsFilterTab.V3;
      const hasV3 = params.farmTypes.includes(FarmsFilterTab.V3);

      if (params.ranges.length && hasV3 === isV3) {
        searchParams.delete(RANGES_PARAM);
      }

      if ((!hasV3 && !isV3) || (isV3 && hasV3)) {
        searchParams.delete(V3_FARM_TYPES_PARAM);
      }

      toggleParam(
        value,
        params.farmTypes,
        ALL_FARM_TYPES,
        FARM_TYPES_PARAM,
        searchParams,
        setSearchParams,
      );
    },
    onUpdateFarmTypes: (values: string[]) => {
      if (
        !values.length ||
        (values.length === ALL_FARM_TYPES.length &&
          values.every((value) =>
            ALL_FARM_TYPES.includes(value as FarmsFilterTab),
          ))
      ) {
        searchParams.delete(FARM_TYPES_PARAM);
      } else {
        searchParams.set(FARM_TYPES_PARAM, values.join(','));
      }
      setSearchParams(searchParams);
    },
    onUpdateRiskLevel: (values: number[]) => {
      const [from, to] = values;

      const updateSearchParam = (
        param: string,
        value: number,
        defaultValue: number,
      ) => {
        if (toBN(value).equals(defaultValue)) {
          searchParams.delete(param);
        } else {
          searchParams.set(param, value.toString());
        }
      };

      updateSearchParam(RISK_LEVEL_FROM_PARAM, from, DEFAULT_LEVEL_FROM);
      updateSearchParam(RISK_LEVEL_TO_PARAM, to, DEFAULT_LEVEL_TO);

      setSearchParams(searchParams);
    },
    onUpdateProfitPercents: (values: number[]) => {
      const [from, to] = values;

      const updateSearchParam = (
        param: string,
        value: number,
        defaultValue: number,
      ) => {
        if (toBN(value).equals(defaultValue)) {
          searchParams.delete(param);
        } else {
          searchParams.set(param, value.toString());
        }
      };
      updateSearchParam(PROFIT_PERCENT_FROM_PARAM, from, DEFAULT_LEVEL_FROM);
      updateSearchParam(PROFIT_PERCENT_TO_PARAM, to, DEFAULT_LEVEL_TO);

      setSearchParams(searchParams);
    },
    onUpdateTags: (values: { value: string; label: string }[]) => {
      const tags = values.map((item) => item.value);

      if (tags.length) {
        searchParams.set(TAGS_PARAM, tags.join(','));
      } else {
        searchParams.delete(TAGS_PARAM);
      }

      setSearchParams(searchParams);
    },
    onUpdateRanges: (values: { value: string; label: string }[]) => {
      const ranges = values.map((item) => item.value);

      if (ranges.length) {
        searchParams.set(RANGES_PARAM, ranges.join(','));
      } else {
        searchParams.delete(RANGES_PARAM);
      }

      setSearchParams(searchParams);
    },
    onUpdatePlatforms: (platforms: number[]) => {
      if (platforms.length) {
        searchParams.set(PLATFORMS_PARAM, platforms.join(','));
      } else {
        searchParams.delete(PLATFORMS_PARAM);
      }

      setSearchParams(searchParams);
    },
    onUpdateProfitTokens: (tokens: string[]) => {
      if (tokens.length) {
        searchParams.set(PROFIT_TOKENS_PARAM, tokens.join(','));
      } else {
        searchParams.delete(PROFIT_TOKENS_PARAM);
      }

      setSearchParams(searchParams);
    },
    onUpdateSortDirection: (direction: SortDestination) => {
      if (direction === SortDestination.Desc) {
        searchParams.delete(SORT_DIRECTION_PARAM);
      } else {
        searchParams.set(SORT_DIRECTION_PARAM, direction);
      }
      setSearchParams(searchParams);
    },
    onToggleTab: (type: string | number) => {
      if (type === DEFAULT_VAULTS_TAB) {
        if (searchParams.has(TAB_PARAM)) {
          const cleaned = removeAnyTabParam(
            String(filtersInStorage),
            TAB_PARAM,
          );
          setFiltersInStorage(cleaned);
        }

        searchParams.delete(TAB_PARAM);
      } else {
        searchParams.set(TAB_PARAM, String(type));
      }
      setSearchParams(searchParams);
    },
    onClear: () => {
      setFiltersInStorage('');
      searchParams.delete(TAGS_PARAM);
      searchParams.delete(TAB_PARAM);
      searchParams.delete(RANGES_PARAM);
      searchParams.delete(SEARCH_PARAM);
      searchParams.delete(SORT_BY_PARAM);
      searchParams.delete(PLATFORMS_PARAM);
      searchParams.delete(FARM_TYPES_PARAM);
      searchParams.delete(V3_FARM_TYPES_PARAM);
      searchParams.delete(PROFIT_TOKENS_PARAM);
      searchParams.delete(RISK_LEVEL_TO_PARAM);
      searchParams.delete(STRATEGY_FARM_PARAM);
      searchParams.delete(SORT_DIRECTION_PARAM);
      searchParams.delete(RISK_LEVEL_FROM_PARAM);
      searchParams.delete(PROFIT_PERCENT_TO_PARAM);
      searchParams.delete(PROFIT_PERCENT_FROM_PARAM);
      searchParams.delete(CHAIN_FILTER_PARAM);

      setSearchParams(searchParams);
    },
    onUpdateFilters: () => {
      searchParams.delete(TAGS_PARAM);
      searchParams.delete(RANGES_PARAM);
      searchParams.delete(SEARCH_PARAM);
      searchParams.delete(SORT_BY_PARAM);
      searchParams.delete(PLATFORMS_PARAM);
      searchParams.delete(PROFIT_TOKENS_PARAM);
      searchParams.delete(SORT_DIRECTION_PARAM);

      setSearchParams(searchParams);
    },
    onChangeChain: (chainId: number) => {
      let updatedChains: number[];

      const chainsParam = searchParams.get(CHAIN_FILTER_PARAM);
      const currentChains: number[] = chainsParam
        ? JSON.parse(chainsParam)
        : selectedChains;

      if (currentChains.includes(chainId)) {
        updatedChains =
          currentChains.length === CHAINS_FILTER.length
            ? [chainId]
            : currentChains.filter((id) => id !== chainId);
      } else {
        updatedChains = [...currentChains, chainId];
      }

      if (
        !updatedChains.length ||
        areAllChainsSelected(CHAINS_FILTER, updatedChains)
      ) {
        searchParams.delete(CHAIN_FILTER_PARAM);
      } else {
        searchParams.set(CHAIN_FILTER_PARAM, JSON.stringify(updatedChains));
      }

      searchParams.delete(TAGS_PARAM);
      searchParams.delete(SEARCH_PARAM);
      searchParams.delete(PLATFORMS_PARAM);
      searchParams.delete(PROFIT_TOKENS_PARAM);

      setSearchParams(searchParams);
    },
  };

  return (
    <VaultsFiltersContext.Provider
      value={{
        ...params,
        counterFilters,
        debounceSearchValue,
        singleFarmApi,
        minRiskLevelApi,
        maxRiskLevelApi,
        minProfitPercentApi,
        maxProfitPercentApi,
        isArchivedMode,
        selectedChains,
        disabledV3TypeFilter,
      }}
    >
      {children}
    </VaultsFiltersContext.Provider>
  );
};

export default VaultsFiltersContextProvider;
